import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { v4 as uuid } from 'uuid';
import MyAccountTabs from 'layouts/MyAccountTabs';
import Link from 'next/link';
import { Box } from '@mui/material';
import InvoiceBox from 'components/InvoiceBox';
import AlertAlt from 'components/AlertAlt';
import Snackbar from 'components/Poster/Snackbar';
import useStripeSubscription from 'hooks/useStripeSubscription';
import ProfileService from 'services/ProfileService';
import PaymentService from 'services/PaymentService';
import createInvoicePdf from 'utils/createInvoicePdf';
import { tabItems } from '.';
import createInvoicePdfChina from 'utils/createInvoicePdf/createInvoicePdfChina';
import { getTabItems } from 'utils/myAccount';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { withAuth } from 'features/auth/withAuth';

function History() {
  const router = useRouter();
  const { user, updateUser, profile } = useAuthUser();
  const paymentService = new PaymentService();
  const profileService = new ProfileService();
  const currentStripeSubscription = useStripeSubscription();
  const [billingHistory, setBillingHistory] = useState([]);
  const [latestInvoice, setLatestInvoice] = useState(null);
  const [latestPaymentIntent, setLatestPaymentIntent] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('Processing payment');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const isInvoiceOpen = latestInvoice?.status === 'open' && latestInvoice?.attempt_count === 0;
  const isInvoiceError =
    latestInvoice?.status === 'unpaid' ||
    (latestInvoice?.attempt_count > 0 && latestInvoice?.status !== 'paid');
  const [isChina, setIsChina] = useState(false);
  const userIsChina = user.country_code === 'CN' || isChina;

  async function fetchHistory() {
    try {
      const { data } = await profileService.getBillingHistory(user.id);
      setBillingHistory(data);
    } catch (error) {
      console.log(error.message);
    }
  }

  useEffect(() => {
    async function fetchCheckoutData() {
      try {
        const { data } = await paymentService.data();
        setIsChina(data.countryCode === 'CN');
      } catch (e) {
        console.error(e);
      }
    }

    async function fetchData() {
      await fetchHistory();
      await fetchCheckoutData();
    }

    fetchData();
  }, []);

  useEffect(() => {
    let invoice = currentStripeSubscription?.latest_invoice;
    if (invoice) {
      invoice.formattedDate = getFormattedDate(invoice?.created);
      invoice.formattedPlan = invoice?.lines?.data[0].plan.nickname;
      invoice.formattedTotal = invoice?.total / 100;
      invoice.currency = invoice?.currency.toUpperCase();
      setLatestInvoice(invoice);
      setLatestPaymentIntent(invoice.payment_intent);
    }
  }, [currentStripeSubscription]);

  function generatePdf(invoice) {
    if (!userIsChina) {
      createInvoicePdf(invoice, profile, user.email);
    } else {
      createInvoicePdfChina(invoice, profile, user.email);
    }
  }

  async function retryPayment(id) {
    setSnackbarOpen(true);
    try {
      await paymentService.chargeStripeInvoice({ id });
      const response = await paymentService.getStripeSubscription();
      if (response.data) {
        await fetchHistory();
        setLatestInvoice(response.data.latest_invoice);
        setLatestPaymentIntent(response.data.latest_invoice.payment_intent);
        const { data } = await profileService.getUser(user.id);
        updateUser({ ...data });
      }
    } catch (e) {
      setSnackbarSeverity('error');
      setSnackbarMessage(
        'Payment error. Check your credit card information to avoid account suspension.',
      );
    }
  }

  function getFormattedDate(date) {
    const dateObj = new Date(date * 1000);

    let year = dateObj.getFullYear();
    let month = addZero(dateObj.getMonth() + 1);
    let day = addZero(dateObj.getDate());
    let hours = addZero(dateObj.getHours());
    let minutes = addZero(dateObj.getMinutes());
    let seconds = addZero(dateObj.getSeconds());

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  }

  function addZero(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  async function handleActionClick(invoice) {
    if (invoice.gateway === 'Stripe' && invoice.currency === 'JPY' && invoice.pdfUrl) {
      try {
        window.open(invoice.pdfUrl, '_blank').focus();
      } catch (error) {
        console.error(error);
      }
    } else {
      generatePdf(invoice);
    }
  }

  return (
    <MyAccountTabs
      title="Billing History and Invoices"
      tabItems={getTabItems(user, billingHistory, tabItems)}
    >
      <Head>
        <title>Plan and Billing - My Account - Mind The Graph</title>
      </Head>

      {isInvoiceError && (
        <InvoiceBox
          invoice={latestInvoice}
          status="error"
          onClick={() => {
            retryPayment(latestInvoice.id);
          }}
          alert={
            <>
              <Snackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              />

              <hr />

              <Box>
                <AlertAlt severity="error">
                  Payment failure on {getFormattedDate(latestPaymentIntent.created)}. Your account
                  is set for suspension in a few days. You may need to{' '}
                  <Link href="/my-account/billing/update-card" passHref>
                    update the credit card details
                  </Link>{' '}
                  to avoid account suspension.
                </AlertAlt>
              </Box>
            </>
          }
        />
      )}

      {isInvoiceOpen && (
        <InvoiceBox
          invoice={latestInvoice}
          status={latestInvoice.status}
          onClick={() => {
            router.push('/my-account/billing/update-card');
          }}
          alert={
            <>
              <hr />
              <Box>
                <AlertAlt severity="warning">
                  You have an open invoice.{' '}
                  <Link href="/my-account/billing/update-card" passHref>
                    Add your credit card information
                  </Link>{' '}
                  to confirm the subscription.
                </AlertAlt>
              </Box>
            </>
          }
        />
      )}

      {billingHistory &&
        billingHistory.map((invoice) => (
          <InvoiceBox
            user={user}
            key={uuid()}
            invoice={invoice}
            status="paid"
            onClick={() => handleActionClick(invoice)}
          />
        ))}
    </MyAccountTabs>
  );
}

export default withAuth(History);
